import { useNavigate, useLocation } from "react-router-dom";
import { NavigationMenu, Logo } from "@printtat/ui";
import { cn } from "@printtat/ui";
import {
  ImageIcon,
  Package,
  ShoppingBag,
  CreditCard,
  Users,
  Tag,
  Store,
  Layers,
  Mail,
  ArrowLeft,
  RefreshCw,
} from "lucide-react";
import { UserMenu } from "@/components/UserMenu";

interface NavItem {
  path: string;
  label: string;
  icon: typeof ImageIcon;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const navGroups: NavGroup[] = [
  {
    label: "Commerce",
    items: [
      { path: "/orders", label: "Orders", icon: Package },
      { path: "/payments", label: "Payments", icon: CreditCard },
      { path: "/discounts", label: "Discounts", icon: Tag },
    ],
  },
  {
    label: "Platform",
    items: [
      { path: "/users", label: "Users", icon: Users },
    ],
  },
  {
    label: "Content",
    items: [
      { path: "/assets", label: "Assets", icon: ImageIcon },
      { path: "/products", label: "Products", icon: ShoppingBag },
      { path: "/catalog", label: "Catalog", icon: Store },
    ],
  },
  {
    label: "Queues",
    items: [
      { path: "/render-queue", label: "Mockup Renders", icon: Layers },
      { path: "/payment-reconciliation", label: "Payment Reconciliation", icon: RefreshCw },
    ],
  },
  {
    label: "Operations",
    items: [
      { path: "/email-usage", label: "Email volume", icon: Mail },
    ],
  },
];

export const AdminSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (path: string) => location.pathname.startsWith(path);

  return (
    <div className="h-screen sticky top-0 p-3 w-60 flex-shrink-0">
      <div className="border rounded-2xl bg-white h-full flex flex-col p-2.5">
        {/* Header */}
        <div className="px-2 py-1.5 mb-6">
          <Logo label="Admin" />
        </div>

        {/* Navigation */}
        <NavigationMenu.Root orientation="vertical" className="flex flex-col">
          <NavigationMenu.List className="flex flex-col">
            {navGroups.map((group) => (
              <div key={group.label} className="mb-3">
                <span className="px-2.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                  {group.label}
                </span>
                <div className="mt-1 flex flex-col space-y-0.5">
                  {group.items.map(({ path, label, icon: Icon }) => (
                    <NavigationMenu.Item key={path}>
                      <NavigationMenu.Link
                        asChild
                        active={isActive(path)}
                        className={cn(
                          "w-full text-left px-2.5 py-1.5 text-[13px] font-medium rounded-lg transition-colors",
                          "hover:text-gray-900 hover:bg-gray-100",
                          isActive(path)
                            ? "text-gray-900 bg-gray-100"
                            : "text-gray-500",
                        )}
                      >
                        <button
                          onClick={() => navigate(path)}
                          className="flex items-center gap-2 w-full"
                        >
                          <Icon className="w-3.5 h-3.5" />
                          {label}
                        </button>
                      </NavigationMenu.Link>
                    </NavigationMenu.Item>
                  ))}
                </div>
              </div>
            ))}
          </NavigationMenu.List>
        </NavigationMenu.Root>

        {/* Back to storefront + user menu */}
        <div className="mt-auto space-y-1">
          <a
            href={
              import.meta.env.VITE_STOREFRONT_URL || "http://localhost:5177"
            }
            className="flex items-center gap-2 w-full px-2.5 py-1.5 text-[13px] font-medium text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to app
          </a>
          <UserMenu />
        </div>
      </div>
    </div>
  );
};
